import React, { useMemo, useState, useEffect, useRef } from "react";
import { Search, Download, Star, Drum, Music2, Layers, ArrowLeft, FileText, Copy, Play, Pause, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FAVS_KEY = "sounddna_lib_favs";

const sanitize = (s, fallback = "track") =>
  (s || fallback).toString().replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40) || fallback;

const titleFromPrompt = (p) => {
  if (!p) return "Untitled";
  const words = p.replace(/https?:\/\/\S+/g, "").trim().split(/\s+/).slice(0, 6).join(" ");
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Untitled";
};

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const MySoundDnaLibrary = ({
  onBack,
  drumLibrary = [],
  sunoTracks = [],
  beatLibrary = [],
  separatedStems = null,
  saveWavFromUrl,
  downloadSongWav,
  onGenerateMidi,
  onErase,
}) => {
  const [tab, setTab] = useState("prompts");
  const [search, setSearch] = useState("");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [midiLoadingId, setMidiLoadingId] = useState(null);
  const audioRef = useRef(null);
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FAVS_KEY) || "[]"); } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(FAVS_KEY, JSON.stringify(favorites));
  }, [favorites]);

  const togglePlay = (r) => {
    if (!r.wavUrl) { toast.error("No audio saved for this item yet"); return; }
    const a = audioRef.current;
    if (!a) return;
    if (playingId === r.id) { a.pause(); setPlayingId(null); return; }
    a.src = r.wavUrl;
    a.play().then(() => setPlayingId(r.id)).catch(() => toast.error("Could not play this audio"));
  };

  const makeMidi = async (r) => {
    const sp = r.prompt || r.text || r.genre;
    if (!sp) { toast.error("No prompt available to build MIDI from"); return; }
    if (!onGenerateMidi) { toast.error("MIDI generator unavailable"); return; }
    setMidiLoadingId(r.id);
    try { await onGenerateMidi(sp); } catch { toast.error("Failed to generate MIDI"); }
    finally { setMidiLoadingId(null); }
  };

  const copyText = (r) => {
    const txt = r.prompt || r.text || r.title || "";
    if (!txt) { toast.error("Nothing to copy"); return; }
    navigator.clipboard?.writeText(txt);
    toast.success("Prompt copied");
  };

  const toggleFav = (id) =>
    setFavorites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev]));

  const rows = useMemo(() => {
    const drums = (drumLibrary || [])
      .filter((e) => e.audioUrl && e.source !== "prompt-lab")
      .map((e) => ({
        id: `drum-${e.id}`,
        tab: "drums",
        kind: "drum",
        rawId: e.id,
        title: e.name || `${e.genre || "Drums"} ${e.tempo || ""} BPM`,
        genre: e.genre || "",
        bpm: e.tempo || null,
        musicKey: e.key || null,
        created: e.timestamp || null,
        wavUrl: e.audioUrl,
        wavName: `${sanitize(e.genre, "drums")}_${e.tempo || ""}bpm.wav`,
        text: [e.name, e.genre, e.style_prompt, e.suno_prompt].filter(Boolean).join(" "),
      }));

    const prompts = (drumLibrary || [])
      .filter((e) => e.source === "prompt-lab")
      .map((e) => ({
        id: `prompt-${e.id}`,
        tab: "prompts",
        kind: "drum",
        rawId: e.id,
        title: e.name || `${e.genre || "Prompt"}`,
        genre: e.genre || "",
        bpm: e.bpm || e.tempo || null,
        musicKey: e.key || e.musicKey || null,
        created: e.timestamp || null,
        wavUrl: e.audioUrl || null,
        wavName: `${sanitize(e.genre, "prompt")}.wav`,
        prompt: e.suno_prompt || e.style_prompt || "",
        text: [e.name, e.genre, e.style_prompt, e.suno_prompt].filter(Boolean).join(" "),
      }));

    const songsSuno = (sunoTracks || [])
      .filter((t) => t.audio_url)
      .map((t) => ({
        id: `suno-${t.task_id}`,
        tab: "songs",
        kind: "suno",
        raw: t,
        title: t.title || titleFromPrompt(t.prompt),
        genre: t.style || "",
        bpm: t.bpm || null,
        musicKey: t.key || null,
        created: t.created_at || null,
        wavUrl: t.audio_url,
        wavName: `${sanitize(t.title || t.prompt, "song")}.wav`,
        text: [t.title, t.style, t.prompt].filter(Boolean).join(" "),
        badge: "Suno",
      }));

    const songsBeats = (beatLibrary || [])
      .map((b) => {
        const url = b.audio_url || b.audioUrl || b.audio_url_a || b.audio_url_b;
        return url
          ? {
              id: `beat-${b.task_id || b._id}`,
              tab: "songs",
              kind: "beat",
              rawId: b.task_id || b._id,
              title: titleFromPrompt(b.prompt),
              genre: b.genre || "",
              bpm: b.bpm || null,
              musicKey: b.key || null,
              created: b.created_at || b.completed_at || null,
              wavUrl: url,
              wavName: `${sanitize(b.prompt, "beat")}.wav`,
              text: [b.prompt, b.model].filter(Boolean).join(" "),
              badge: "Beat",
            }
          : null;
      })
      .filter(Boolean);

    const stemsObj = separatedStems && typeof separatedStems === "object" ? separatedStems : {};
    const stems = Object.entries(stemsObj)
      .filter(([, url]) => !!url)
      .map(([name, url]) => ({
        id: `stem-${name}`,
        tab: "stems",
        kind: "stem",
        rawId: name,
        title: name.charAt(0).toUpperCase() + name.slice(1),
        genre: "stem",
        bpm: null,
        musicKey: null,
        created: null,
        wavUrl: url,
        wavName: `${sanitize(name, "stem")}.wav`,
        text: name,
      }));

    return { drums, prompts, songs: [...songsSuno, ...songsBeats], stems };
  }, [drumLibrary, sunoTracks, beatLibrary, separatedStems]);

  const counts = { drums: rows.drums.length, prompts: rows.prompts.length, songs: rows.songs.length, stems: rows.stems.length };

  const visible = useMemo(() => {
    let list = rows[tab] || [];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) => `${r.text} ${r.genre}`.toLowerCase().includes(q));
    if (favoritesOnly) list = list.filter((r) => favorites.includes(r.id));
    return list;
  }, [rows, tab, search, favoritesOnly, favorites]);

  const TABS = [
    { key: "prompts", label: "Prompts", Icon: FileText, color: "#a855f7" },
    { key: "songs", label: "Songs", Icon: Music2, color: "#00d4ff" },
    { key: "drums", label: "Drums", Icon: Drum, color: "#f59e0b" },
    { key: "stems", label: "Stems", Icon: Layers, color: "#8b5cf6" },
  ];

  return (
    <div data-testid="mysounddna-library" style={{ maxWidth: "1000px", margin: "0 auto", width: "100%", padding: "clamp(12px,3vw,24px)" }}>
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} style={{ display: "none" }} />
      <button
        onClick={onBack}
        data-testid="mysounddna-back-btn"
        style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", color: "#fff", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", marginBottom: "16px" }}
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div style={{ marginBottom: "18px" }}>
        <h1 style={{ fontSize: "clamp(1.6rem,4vw,2.2rem)", fontWeight: 900, color: "#fff", margin: 0, letterSpacing: "-0.02em" }}>
          My <span style={{ color: "#00d4ff" }}>MANNIS</span> Library
        </h1>
        <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", margin: "6px 0 0" }}>
          Every prompt, song, drum and stem you've made — including your saved Prompt Lab prompts. Search, star your keepers, and export WAVs.
        </p>
      </div>

      {/* Search + favorites */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
        <div style={{ position: "relative", flex: "1 1 260px" }}>
          <Search className="w-4 h-4" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)" }} />
          <input
            data-testid="mysounddna-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by genre or prompt text…"
            style={{ width: "100%", padding: "12px 12px 12px 36px", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(0,212,255,0.3)", borderRadius: "10px", color: "#fff", fontSize: "0.85rem", outline: "none" }}
          />
        </div>
        <button
          data-testid="mysounddna-favorites-toggle"
          onClick={() => setFavoritesOnly((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "12px 16px", background: favoritesOnly ? "linear-gradient(135deg,#f59e0b,#d97706)" : "rgba(255,255,255,0.06)", border: `1px solid ${favoritesOnly ? "#f59e0b" : "rgba(255,255,255,0.12)"}`, borderRadius: "10px", color: favoritesOnly ? "#111" : "#fff", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}
        >
          <Star className="w-4 h-4" style={{ fill: favoritesOnly ? "#111" : "none" }} /> Favorites
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
        {TABS.map(({ key, label, Icon, color }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              data-testid={`mysounddna-tab-${key}`}
              onClick={() => setTab(key)}
              style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", background: active ? color : "rgba(255,255,255,0.05)", border: `1px solid ${active ? color : "rgba(255,255,255,0.12)"}`, borderRadius: "999px", color: active ? "#0a0e1a" : "#fff", fontSize: "0.85rem", fontWeight: 800, cursor: "pointer" }}
            >
              <Icon className="w-4 h-4" /> {label} ({counts[key]})
            </button>
          );
        })}
      </div>

      {/* Rows */}
      {visible.length === 0 ? (
        <div data-testid="mysounddna-empty" style={{ padding: "48px 20px", textAlign: "center", color: "rgba(255,255,255,0.45)", border: "1px dashed rgba(255,255,255,0.15)", borderRadius: "14px", fontSize: "0.9rem" }}>
          {favoritesOnly ? "No favorites in this tab yet — tap the star on a track to keep it here." : `No ${tab} yet. Create some and they'll show up here.`}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {visible.map((r) => {
            const isFav = favorites.includes(r.id);
            return (
              <div
                key={r.id}
                data-testid={`mysounddna-row-${r.id}`}
                style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 16px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: "12px", flexWrap: "wrap" }}
              >
                <button
                  data-testid={`mysounddna-fav-${r.id}`}
                  onClick={() => toggleFav(r.id)}
                  title={isFav ? "Remove from favorites" : "Mark as favorite"}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", flexShrink: 0 }}
                >
                  <Star className="w-5 h-5" style={{ color: isFav ? "#f59e0b" : "rgba(255,255,255,0.3)", fill: isFav ? "#f59e0b" : "none" }} />
                </button>

                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center", gap: "8px" }}>
                    {r.title}
                    {r.badge && <span style={{ fontSize: "0.6rem", fontWeight: 800, padding: "2px 6px", borderRadius: "5px", background: "rgba(0,212,255,0.15)", color: "#00d4ff" }}>{r.badge}</span>}
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", marginTop: "3px" }}>
                    {r.genre ? <span style={{ textTransform: "capitalize" }}>{r.genre}</span> : "—"} · {fmtDate(r.created)}
                  </div>
                  {r.prompt && (
                    <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.74rem", marginTop: "6px", lineHeight: 1.45, maxWidth: "640px" }}>{r.prompt}</div>
                  )}
                </div>

                <div style={{ display: "flex", gap: "16px", flexShrink: 0 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.05em" }}>BPM</div>
                    <div style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 700 }}>{r.bpm || "—"}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.05em" }}>KEY</div>
                    <div style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 700 }}>{r.musicKey || "—"}</div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", flexShrink: 0, width: "clamp(220px, 100%, 320px)" }}>
                  <button
                    data-testid={`mysounddna-copy-${r.id}`}
                    onClick={() => copyText(r)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 12px", background: "rgba(168,85,247,0.18)", border: "1px solid rgba(168,85,247,0.5)", borderRadius: "10px", color: "#e9d5ff", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}
                  >
                    <Copy className="w-4 h-4" /> COPY PROMPT
                  </button>

                  <button
                    data-testid={`mysounddna-download-${r.id}`}
                    onClick={() => {
                      if (!r.wavUrl) { toast.error("No audio saved for this item yet"); return; }
                      // Songs are provider MP3s → transcode to a true full-song WAV.
                      // Drums/prompts/stems are already real WAVs from our backend.
                      const baseName = (r.wavName || "track").replace(/\.[^.]+$/, "") + ".wav";
                      if (r.tab === "songs" && downloadSongWav) {
                        downloadSongWav(r.wavUrl, baseName, "wav");
                      } else {
                        saveWavFromUrl?.(r.wavUrl, r.wavName);
                      }
                    }}
                    disabled={!r.wavUrl}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 12px", background: r.wavUrl ? "linear-gradient(135deg,#22c55e,#059669)" : "rgba(255,255,255,0.06)", border: r.wavUrl ? "none" : "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", color: r.wavUrl ? "#fff" : "rgba(255,255,255,0.35)", fontSize: "0.72rem", fontWeight: 800, cursor: r.wavUrl ? "pointer" : "not-allowed", boxShadow: r.wavUrl ? "0 4px 14px rgba(34,197,94,0.35)" : "none" }}
                  >
                    <Download className="w-4 h-4" /> DOWNLOAD WAV
                  </button>

                  <button
                    data-testid={`mysounddna-play-${r.id}`}
                    onClick={() => togglePlay(r)}
                    disabled={!r.wavUrl}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 12px", background: !r.wavUrl ? "rgba(255,255,255,0.06)" : playingId === r.id ? "linear-gradient(135deg,#00d4ff,#0ea5e9)" : "rgba(0,212,255,0.18)", border: !r.wavUrl ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,212,255,0.5)", borderRadius: "10px", color: !r.wavUrl ? "rgba(255,255,255,0.35)" : playingId === r.id ? "#0a0e1a" : "#a5f3fc", fontSize: "0.72rem", fontWeight: 800, cursor: r.wavUrl ? "pointer" : "not-allowed" }}
                  >
                    {playingId === r.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playingId === r.id ? "PAUSE" : "AUDIO PLAY"}
                  </button>

                  <button
                    data-testid={`mysounddna-midi-${r.id}`}
                    onClick={() => makeMidi(r)}
                    disabled={midiLoadingId === r.id}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 12px", background: "linear-gradient(135deg,#f59e0b,#d97706)", border: "none", borderRadius: "10px", color: "#111", fontSize: "0.72rem", fontWeight: 800, cursor: midiLoadingId === r.id ? "wait" : "pointer", opacity: midiLoadingId === r.id ? 0.7 : 1 }}
                  >
                    {midiLoadingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Music2 className="w-4 h-4" />} {midiLoadingId === r.id ? "BUILDING…" : "MIDI"}
                  </button>

                  <button
                    data-testid={`mysounddna-erase-${r.id}`}
                    onClick={() => (playingId === r.id && audioRef.current?.pause(), onErase?.(r))}
                    style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "10px 12px", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.45)", borderRadius: "10px", color: "#fca5a5", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}
                  >
                    <Trash2 className="w-4 h-4" /> ERASE
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MySoundDnaLibrary;
